import { useEffect, type ReactNode, type CSSProperties } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { haptics } from '../../lib/haptics'

interface Props {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  maxHeight?: string
  style?: CSSProperties
  /** When false: backdrop click, drag, and Escape are all disabled */
  dismissible?: boolean
}

export default function BottomSheet({ open, onClose, title, children, maxHeight = '75vh', style, dismissible = true }: Props) {
  // Close on Escape (only when dismissible)
  useEffect(() => {
    if (!open || !dismissible) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose, dismissible])

  // Haptic when opening
  useEffect(() => {
    if (open) {
      haptics.impact('soft')
    }
  }, [open])

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end">
          {/* Backdrop — clickable only when dismissible */}
          <motion.div
            className="absolute inset-0"
            style={{ backgroundColor: 'var(--imago-color-overlay)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={dismissible ? onClose : undefined}
          />

          {/* Sheet */}
          <motion.div
            className="relative flex flex-col overflow-hidden"
            style={{
              backgroundColor: 'var(--tg-theme-bg-color)',
              borderTopLeftRadius: 'var(--radius-lg)',
              borderTopRightRadius: 'var(--radius-lg)',
              maxHeight,
              boxShadow: 'var(--shadow-sheet)',
              paddingBottom: 'var(--safe-bottom)',
              ...style,
            }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            drag={dismissible ? 'y' : false}
            dragConstraints={{ top: 0 }}
            dragElastic={0.2}
            dragListener={false}
            onDragEnd={(_, info) => {
              if (!dismissible) return
              if (info.offset.y > 100 || info.velocity.y > 500) {
                onClose()
              }
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle bar — draggable only when dismissible */}
            {dismissible ? (
              <motion.div
                className="flex justify-center py-2 shrink-0 cursor-grab active:cursor-grabbing"
                drag="y"
                dragConstraints={{ top: 0 }}
                dragElastic={0.2}
                onDragEnd={(_, info) => {
                  if (info.offset.y > 80 || info.velocity.y > 400) {
                    onClose()
                  }
                }}
                style={{ touchAction: 'none' }}
              >
                <div
                  className="w-10 h-1 rounded-full"
                  style={{
                    backgroundColor: 'var(--tg-theme-hint-color)',
                    opacity: 0.3,
                  }}
                />
              </motion.div>
            ) : (
              <div className="flex justify-center py-2 shrink-0">
                <div
                  className="w-10 h-1 rounded-full"
                  style={{
                    backgroundColor: 'var(--tg-theme-hint-color)',
                    opacity: 0.15,
                  }}
                />
              </div>
            )}

            {/* Title */}
            {title && (
              <div className="px-sp-4 pt-sp-2 pb-sp-3 shrink-0 text-center">
                <p
                  className="text-[17px] font-semibold"
                  style={{ color: 'var(--tg-theme-text-color)' }}
                >
                  {title}
                </p>
              </div>
            )}

            {/* Content — pointer events isolated to prevent sheet drag conflict */}
            <div
              className="overflow-y-auto scroll-area flex-1"
              onPointerDown={(e) => e.stopPropagation()}
            >
              {children}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
